import React from "react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import QuoteForm from "../components/site/QuoteForm";
import GalleryGrid from "../components/site/GalleryGrid";
import BeforeAfterSlider from "../components/site/BeforeAfterSlider";
import TestimonialsMarquee from "../components/site/TestimonialsMarquee";
import PhotoShowcase from "../components/site/PhotoShowcase";
import SectionDivider from "../components/site/SectionDivider";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import photos from "../data/photography.json";

const Swatch = ({ name, hex, code, light }) => (
  <div data-testid={`swatch-${name.toLowerCase()}`} className="space-y-3">
    <div
      className={`aspect-[4/5] border ${light ? "border-ink/10" : "border-bone/10"}`}
      style={{ background: hex }}
    />
    <div className="flex items-baseline justify-between">
      <p className="font-display text-lg tracking-editorial">{name}</p>
      <p className="font-mono text-[10px] uppercase tracking-wider3 text-char-mute">{hex}</p>
    </div>
    <p className="font-mono text-[10px] uppercase tracking-wider3 text-char-mute">{code}</p>
  </div>
);

const TypeRow = ({ token, sample, className = "", note }) => (
  <div className="grid grid-cols-12 gap-6 items-baseline py-6 border-b border-ink/10">
    <p className="col-span-12 md:col-span-3 font-mono text-[10px] uppercase tracking-wider3 text-char-mute">
      {token}
    </p>
    <div className={`col-span-12 md:col-span-7 ${className}`}>{sample}</div>
    <p className="col-span-12 md:col-span-2 font-mono text-[10px] uppercase tracking-wider3 text-char-mute">
      {note}
    </p>
  </div>
);

export default function StyleGuide() {
  return (
    <div className="min-h-screen bg-bone text-ink">
      <Header />

      {/* HERO — transparent overlay over real photography */}
      <section
        data-testid="styleguide-hero"
        className="relative min-h-[100svh] bg-ink text-bone overflow-hidden"
      >
        <div className="absolute inset-0">
          <Photo
            slug="hero-banquet-01"
            alt="Melbourne hospitality banquet photography"
            priority
            ratio="auto"
            className="!relative h-full w-full"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/65 via-ink/30 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />

        <div className="relative mx-auto max-w-[1600px] px-6 lg:px-10 min-h-[100svh] flex flex-col justify-between pt-32 pb-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider3 text-bone/70">
              Visual Foundation · Vol. 01 · restaurantphotographer.com.au
            </p>
          </div>

          <div className="max-w-5xl">
            <h1 className="font-display text-display-xl tracking-editorial leading-[0.92]">
              The plate, the
              <br />
              <span className="italic font-light text-ember">room, the hour</span>
              <br />
              before service.
            </h1>
            <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-bone/80">
              A mobile photographer for restaurants, cafés and hospitality
              businesses across Melbourne. This page is the visual foundation —
              typography, palette, components — that every section of the site
              is built from.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton variant="primary" size="lg">Get A Shoot Quote</CTAButton>
              <CTAButton variant="ghost" size="lg" arrow={false}>
                View Photography Services
              </CTAButton>
            </div>
          </div>

          <div className="flex items-end justify-between text-bone/60">
            <div className="grid grid-cols-3 gap-8 max-w-2xl text-xs">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider3 text-ember mb-1">Coverage</p>
                <p className="font-display text-lg text-bone">All of Melbourne</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider3 text-ember mb-1">Work</p>
                <p className="font-display text-lg text-bone">Menus · Venues · Brands</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider3 text-ember mb-1">Booking</p>
                <p className="font-display text-lg text-bone">2–4 weeks ahead</p>
              </div>
            </div>
            <p className="hidden md:block font-mono text-[10px] uppercase tracking-wider3 rotate-180" style={{ writingMode: "vertical-rl" }}>
              Scroll
            </p>
          </div>
        </div>
      </section>

      {/* COLOUR PALETTE */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-10 py-24 lg:py-36">
        <ScrollReveal>
          <SectionDivider index="01" label="Palette · Hospitality colour system" />
        </ScrollReveal>

        <div className="mt-16 grid grid-cols-12 gap-10 lg:gap-14">
          <div className="col-span-12 lg:col-span-4">
            <h2 className="font-display text-display-md tracking-editorial leading-tight">
              Warm, low, and
              <br /><span className="italic font-light">honest about light.</span>
            </h2>
            <p className="mt-6 max-w-md text-char text-sm md:text-base leading-relaxed">
              Drawn from Melbourne hospitality interiors — bluestone, kitchen brass,
              ember from a wood oven, bone-warm linen. No gradients. No neon. No
              startup blues.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <Swatch name="Ink" hex="#0E0E0C" code="--ink · primary surface" />
            <Swatch name="Charcoal" hex="#2A2825" code="--char · secondary dark" />
            <Swatch name="Bone" hex="#F4EFE8" code="--bone · primary light" light />
            <Swatch name="Ember" hex="#C0532B" code="--ember · burnt orange" />
          </div>
        </div>
      </section>

      {/* TYPOGRAPHY */}
      <section className="bg-ink text-bone py-24 lg:py-36">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
          <ScrollReveal>
            <SectionDivider index="02" label="Typography · Editorial hierarchy" dark />
          </ScrollReveal>

          <div className="mt-16 grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-4">
              <h2 className="font-display text-display-md tracking-editorial leading-tight">
                Playfair for the
                <br /><span className="italic font-light text-ember">plate.</span>
                <br />Montserrat for everything else.
              </h2>
              <p className="mt-6 text-sm md:text-base text-bone/70 leading-relaxed max-w-md">
                Editorial serif headlines that feel like a printed menu. A clean,
                quiet sans for body copy. Italics carry voice; mono carries metadata.
              </p>
            </div>

            <div className="col-span-12 lg:col-span-8">
              <div className="bg-bone text-ink p-8 md:p-12">
                <TypeRow
                  token="Display / XL"
                  sample={
                    <span className="font-display text-5xl md:text-7xl tracking-editorial leading-[0.95]">
                      A quiet kind <span className="italic font-light text-ember">of hunger</span>
                    </span>
                  }
                  note="Playfair Display"
                />
                <TypeRow
                  token="Display / LG"
                  sample={
                    <span className="font-display text-4xl md:text-5xl tracking-editorial">
                      Shot in the moment <span className="italic">before service.</span>
                    </span>
                  }
                  note="Playfair · Italic"
                />
                <TypeRow
                  token="Subhead"
                  sample={
                    <span className="font-display text-2xl md:text-3xl tracking-editorial">
                      Menu photography that matches the room.
                    </span>
                  }
                  note="Playfair · 500"
                />
                <TypeRow
                  token="Body"
                  sample={
                    <span className="font-sans text-base leading-relaxed">
                      Working with restaurants, cafés and venues across Melbourne to
                      build cohesive image libraries — menu refreshes through to full
                      brand campaigns, all shot on location across Melbourne.
                    </span>
                  }
                  note="Montserrat · 400"
                />
                <TypeRow
                  token="Eyebrow / Meta"
                  sample={
                    <span className="font-mono text-[10px] uppercase tracking-wider3 text-ember">
                      [ 01 ] · Selected work — Melbourne · 2025
                    </span>
                  }
                  note="JetBrains Mono · 10/wider3"
                />
                <TypeRow
                  token="Pull quote"
                  sample={
                    <span className="font-editorial italic text-2xl md:text-3xl leading-snug">
                      &ldquo;The photos doubled our online bookings within a month.&rdquo;
                    </span>
                  }
                  note="Cormorant · italic"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUTTONS */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-10 py-24 lg:py-36">
        <ScrollReveal>
          <SectionDivider index="03" label="Components · Calls to action" />
        </ScrollReveal>

        <div className="mt-16 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <h2 className="font-display text-display-md tracking-editorial leading-tight">
              Tactile, sharp,
              <br /><span className="italic font-light">never plastic.</span>
            </h2>
            <p className="mt-6 max-w-md text-char text-sm leading-relaxed">
              No rounded pills. No gradients. A single small ember square for
              primary action, a hairline outline for secondary, a quiet underline
              for tertiary.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8 space-y-12">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider3 text-char-mute mb-5">Primary</p>
              <div className="flex flex-wrap gap-3">
                <CTAButton variant="primary" size="sm">Get A Shoot Quote</CTAButton>
                <CTAButton variant="primary" size="md">Get A Shoot Quote</CTAButton>
                <CTAButton variant="primary" size="lg">Get A Shoot Quote</CTAButton>
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider3 text-char-mute mb-5">Secondary</p>
              <div className="flex flex-wrap gap-3">
                <CTAButton variant="secondary" size="sm">View Photography Services</CTAButton>
                <CTAButton variant="secondary" size="md">View Photography Services</CTAButton>
                <CTAButton variant="secondary" size="lg">View Photography Services</CTAButton>
              </div>
            </div>
            <div className="bg-ink p-10 -mx-4 md:mx-0">
              <p className="font-mono text-[10px] uppercase tracking-wider3 text-bone/60 mb-5">On dark</p>
              <div className="flex flex-wrap gap-3">
                <CTAButton variant="primary" size="md">Book A Shoot</CTAButton>
                <CTAButton variant="ghost" size="md">See Latest Work</CTAButton>
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider3 text-char-mute mb-5">Tertiary · Link</p>
              <CTAButton variant="link">Read the journal</CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO SHOWCASE */}
      <section className="bg-bone-warm py-24 lg:py-36">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
          <ScrollReveal>
            <SectionDivider index="04" label="Layout · Editorial photography showcase" />
          </ScrollReveal>
          <div className="mt-16">
            <PhotoShowcase />
          </div>
        </div>
      </section>

      {/* GALLERY GRID */}
      <section className="bg-ink text-bone py-24 lg:py-36">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
          <ScrollReveal>
            <SectionDivider index="05" label="Layout · Asymmetric gallery system" dark />
          </ScrollReveal>

          <div className="mt-12 mb-16 max-w-2xl">
            <h2 className="font-display text-display-md tracking-editorial leading-tight">
              Layouts that breathe — <span className="italic font-light text-ember">like a kitchen at 5pm.</span>
            </h2>
          </div>

          <GalleryGrid />
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-10 py-24 lg:py-36">
        <ScrollReveal>
          <SectionDivider index="06" label="Component · Before / after slider" />
        </ScrollReveal>

        <div className="mt-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <h2 className="font-display text-display-md tracking-editorial leading-tight">
              The difference, <span className="italic font-light">on the same plate.</span>
            </h2>
            <p className="mt-6 max-w-md text-char text-sm leading-relaxed">
              Used to show menu refreshes against existing phone shots. Drag the
              handle to compare.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <BeforeAfterSlider />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS MARQUEE */}
      <ScrollReveal>
        <TestimonialsMarquee />
      </ScrollReveal>

      {/* QUOTE FORM (LIGHT) */}
      <section className="bg-bone py-24 lg:py-36">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
          <ScrollReveal>
            <SectionDivider index="07" label="Component · Quote request form" />
          </ScrollReveal>

          <div className="mt-16 grid grid-cols-12 gap-10 lg:gap-16">
            <div className="col-span-12 lg:col-span-5">
              <h2 className="font-display text-display-md tracking-editorial leading-tight">
                Tell us about
                <br /><span className="italic font-light text-ember">your shoot.</span>
              </h2>
              <p className="mt-6 max-w-md text-char leading-relaxed">
                Short, direct enquiry. No long forms, no marketing dross.
              </p>

              <div className="mt-10 space-y-6 text-sm">
                {[
                  ["Turnaround", "Hand-written quote within 1 business day"],
                  ["Bookings", "Usually 2–4 weeks ahead — rush available"],
                  ["Pricing", "Transparent, based on usage and shot count"],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-6 pb-4 border-b border-ink/10">
                    <p className="font-mono text-[10px] uppercase tracking-wider3 text-ember w-24 shrink-0">{k}</p>
                    <p className="text-char">{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM (DARK VARIANT) */}
      <section className="bg-ink text-bone py-24 lg:py-36">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
          <ScrollReveal>
            <SectionDivider index="08" label="Component · Quote form · dark variant" dark />
          </ScrollReveal>
          <div className="mt-12 max-w-3xl">
            <QuoteForm dark compact />
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHY LIBRARY — full breadth */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-10 py-24 lg:py-36">
        <ScrollReveal>
          <SectionDivider index="09" label="Photography · Recent client work" />
        </ScrollReveal>
        <div className="mt-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <h2 className="font-display text-display-md tracking-editorial leading-tight">
              Real plates, <span className="italic font-light text-ember">real rooms.</span>
            </h2>
            <p className="mt-6 text-char leading-relaxed max-w-md">
              Two recent shoots show the tonal range — moody documentary at
              Dosa Hut Clayton, bright editorial chaat for Laddu Gopal. Every
              image is served as a responsive WebP at three sizes.
            </p>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-wider3 text-char-mute">
              {photos.length} images · {photos.filter(p => p.category === 'hero').length} heroes ·
              {' '}{photos.filter(p => p.category === 'menu').length} menu ·
              {' '}{photos.filter(p => p.category === 'venue').length} venue
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
            {[
              "hero-raj-kachori-08",
              "hero-laddu-220",
              "hero-gulab-jamun-00",
              "menu-laddu-220",
            ].map((slug) => (
              <Photo
                key={slug}
                slug={slug}
                alt={slug.replace(/-/g, " ")}
                ratio="4/5"
                sizes="(min-width: 1024px) 30vw, 50vw"
              />
            ))}
          </div>
        </div>

        {/* Editorial filmstrip — full library at a glance */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {photos
            .filter((p) => ["menu", "hero"].includes(p.category))
            .slice(0, 18)
            .map((p) => (
              <Photo
                key={p.slug}
                slug={p.slug}
                alt={p.slug.replace(/-/g, " ")}
                ratio="1/1"
                sizes="(min-width: 1024px) 16vw, 33vw"
              />
            ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
